import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ClientStatuses,
  DayOfWeeks,
  IndividualServiceTypes,
  ServiceLabels,
  type ClientStatus,
  type DayOfWeek,
  type IndividualServiceType,
} from '@census/shared';
import { api } from '../../shared/api';
import { Modal, FormField } from '../../ui/Modal';

/**
 * Full client-edit modal. Fields that can change without re-running the
 * scheduler are surfaced here: telehealth, transport, preferred days, nursing
 * day, IOP track pin, case manager, notes, status, EMR id, required services.
 *
 * Cohort and cohortNumber are NOT editable here — those change the schedule
 * fundamentally and should be handled via re-intake. (Showing them read-only
 * to make it obvious.)
 */
export function ClientEditModal({
  open,
  client,
  onClose,
}: {
  open: boolean;
  client: any | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  // IOP track resources for the dropdown.
  const { data: iopTracks } = useQuery({
    queryKey: ['iop-track-resources'],
    queryFn: () =>
      api
        .get('/resources', { params: { type: 'GROUP', activeOnly: 'true' } })
        .then((r) =>
          (r.data as any[])
            .filter((g) => g.groupServiceType === 'IOP')
            .map((g) => ({ id: g.id, displayName: g.displayName })),
        ),
    enabled: open,
  });
  const { data: locations } = useQuery({
    queryKey: ['locations-active'],
    queryFn: () => api.get('/locations', { params: { activeOnly: 'true' } }).then((r) => r.data),
    enabled: open,
  });

  // Case managers (community providers).
  const { data: caseManagers } = useQuery({
    queryKey: ['case-manager-resources'],
    queryFn: () =>
      api
        .get('/resources', { params: { type: 'COMMUNITY_PROVIDER', activeOnly: 'true' } })
        .then((r) =>
          (r.data as any[]).map((g) => ({ id: g.id, displayName: g.displayName })),
        ),
    enabled: open,
  });

  const [form, setForm] = useState<any>({});
  useEffect(() => {
    if (!client) return;
    setForm({
      firstName: client.firstName ?? '',
      lastName: client.lastName ?? '',
      externalRef: client.externalRef ?? '',
      status: client.status ?? 'ACTIVE',
      telehealthAllowed: client.telehealthAllowed ?? true,
      homeCommunityOk: client.homeCommunityOk ?? true,
      transportLimited: client.transportLimited ?? false,
      preferredDays: client.preferredDays ?? [],
      iopTrackResourceId: client.iopTrackResourceId ?? '',
      nursingGroupDay: client.nursingSkip ? 'NOT_NEEDED' : (client.nursingGroupDay ?? ''),
      caseManagerResourceId: client.caseManagerResourceId ?? '',
      locationId: client.locationId ?? '',
      requiredServices: client.requiredServices ?? [],
      notes: client.notes ?? '',
    });
  }, [client?.id, open]);

  const save = useMutation({
    mutationFn: () => {
      // Build a patch that only sends what changed. Empty strings → null
      // for nullable fields so the server can clear them.
      const patch: Record<string, unknown> = {
        firstName: form.firstName,
        lastName: form.lastName,
        externalRef: form.externalRef === '' ? null : form.externalRef,
        status: form.status,
        telehealthAllowed: form.telehealthAllowed,
        homeCommunityOk: form.homeCommunityOk,
        transportLimited: form.transportLimited,
        preferredDays: form.preferredDays,
        iopTrackResourceId: form.iopTrackResourceId === '' ? null : form.iopTrackResourceId,
        nursingGroupDay: (form.nursingGroupDay === '' || form.nursingGroupDay === 'NOT_NEEDED') ? null : form.nursingGroupDay,
        nursingSkip: form.nursingGroupDay === 'NOT_NEEDED',
        caseManagerResourceId:
          form.caseManagerResourceId === '' ? null : form.caseManagerResourceId,
        locationId: form.locationId === '' ? null : form.locationId,
        requiredServices: form.requiredServices,
        notes: form.notes === '' ? null : form.notes,
      };
      return api.patch(`/clients/${client.id}`, patch).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', client?.id] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client updated');
      onClose();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? 'Update failed'),
  });

  if (!client) return null;

  const toggle = <T extends string>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${client.cohortId} — ${client.firstName} ${client.lastName}`}
      subtitle="Cohort cannot change here; for re-cohorting, discharge & re-intake. Slot-level changes (specific appointment times) live on the appointment record."
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!form.firstName || !form.lastName || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="First name" required>
          <input
            className="input"
            value={form.firstName ?? ''}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
        </FormField>
        <FormField label="Last name" required>
          <input
            className="input"
            value={form.lastName ?? ''}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </FormField>
        <FormField label="Cohort (read-only)">
          <input className="input" value={`${client.cohort} — ${client.cohortId}`} disabled />
        </FormField>
        <FormField label="Status">
          <select
            className="input"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as ClientStatus })}
          >
            {ClientStatuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="EMR client ID">
          <input
            className="input"
            placeholder="Paste from EMR (e.g., 18A7C3-9F2)"
            value={form.externalRef ?? ''}
            onChange={(e) => setForm({ ...form, externalRef: e.target.value })}
          />
        </FormField>
        <FormField label="Case manager">
          <select
            className="input"
            value={form.caseManagerResourceId ?? ''}
            onChange={(e) =>
              setForm({ ...form, caseManagerResourceId: e.target.value })
            }
          >
            <option value="">— Unassigned —</option>
            {(caseManagers ?? []).map((cm: any) => (
              <option key={cm.id} value={cm.id}>
                {cm.displayName}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Location">
          <select
            className="input"
            value={form.locationId ?? ''}
            onChange={(e) => setForm({ ...form, locationId: e.target.value })}
          >
            <option value="">— None —</option>
            {(locations ?? []).map((l: any) => (
              <option key={l.id} value={l.id}>{l.city || l.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="IOP track" hint="Cohort C/D only. Blank = engine auto-picks by capacity.">
          <select
            className="input"
            value={form.iopTrackResourceId ?? ''}
            onChange={(e) => setForm({ ...form, iopTrackResourceId: e.target.value })}
          >
            <option value="">— Auto-pick by capacity —</option>
            {(iopTracks ?? []).map((g: any) => (
              <option key={g.id} value={g.id}>
                {g.displayName}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Nursing group day">
          <select
            className="input"
            value={form.nursingGroupDay ?? ''}
            onChange={(e) => setForm({ ...form, nursingGroupDay: e.target.value })}
          >
            <option value="">— Unassigned —</option>
            <option value="NOT_NEEDED">Not Needed</option>
            {(['MON', 'TUE', 'WED', 'THU', 'FRI'] as DayOfWeek[]).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Delivery preferences">
          <div className="space-y-1.5 rounded border border-line px-3 py-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.telehealthAllowed}
                onChange={(e) =>
                  setForm({ ...form, telehealthAllowed: e.target.checked })
                }
              />
              Telehealth allowed
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.homeCommunityOk}
                onChange={(e) => setForm({ ...form, homeCommunityOk: e.target.checked })}
              />
              Home / community OK
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.transportLimited}
                onChange={(e) =>
                  setForm({ ...form, transportLimited: e.target.checked })
                }
              />
              Transport limited (prefer telehealth)
            </label>
          </div>
        </FormField>
        <FormField label="Preferred days">
          <div className="flex flex-wrap gap-1.5">
            {DayOfWeeks.map((d) => {
              const on = (form.preferredDays ?? []).includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      preferredDays: toggle(form.preferredDays ?? [], d),
                    })
                  }
                  className={
                    on
                      ? 'rounded-md bg-brand-600 px-2.5 py-1 text-2xs font-semibold uppercase text-white'
                      : 'rounded-md bg-surface-muted px-2.5 py-1 text-2xs font-semibold uppercase text-ink-muted hover:bg-line'
                  }
                >
                  {d}
                </button>
              );
            })}
          </div>
        </FormField>
        <FormField label="Required individual services">
          <div className="grid grid-cols-2 gap-1 rounded border border-line p-2">
            {IndividualServiceTypes.map((s) => {
              const on = (form.requiredServices ?? []).includes(s);
              return (
                <label key={s} className="flex items-center gap-2 text-2xs">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setForm({
                        ...form,
                        requiredServices: toggle(
                          (form.requiredServices ?? []) as IndividualServiceType[],
                          s,
                        ),
                      })
                    }
                  />
                  <span className="truncate">{ServiceLabels[s]}</span>
                </label>
              );
            })}
          </div>
        </FormField>
        <FormField label="Notes">
          <textarea
            className="input min-h-[80px]"
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </FormField>
      </div>
    </Modal>
  );
}
